import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BuySourcesRail } from "@/components/buy-sources";
import { ProductImageLoupe } from "@/components/product/ProductImageLoupe";
import {
  ProductSourceIconBar,
  buildStorefrontHunterSourceActions,
} from "@/components/product/ProductSourceIconBar";
import {
  collectCatalogSizes,
  CatalogSizesPopover,
  ProductDescriptionPopover,
  CATALOG_POPOVER_ID,
  DESCRIPTION_POPOVER_ID,
} from "@/components/analyzer/MatchHeader";
import { normalizeProductDescription } from "@/lib/productDescription";
import { usd } from "@/lib/format";
import { RatingStar } from "@/components/common/primitives";
import { QuickROI } from "./QuickROI";
import { formatRating } from "./models";
import {
  BUY_SOURCES_STATUS,
  countBuySourcesFromEntry,
  emptyBuySourcesEntry,
  visibleBuySourcesFromEntry,
} from "@/lib/storefrontHunter/autoBuySources";
import {
  formatBuyBoxSoldBy,
  isWalmartBuyBoxSeller,
} from "@/lib/storefrontHunter/buyBoxSeller";
import { Store } from "lucide-react";

function money(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "—";
  return usd(v);
}

function formatSold(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string") return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (n < 50) return "<50";
  return n.toLocaleString();
}

/**
 * Monthly Sold display.
 *
 * Walmart publishes "X+ bought in past month", which is a LOWER BOUND. The
 * trailing "+" is preserved so a lower bound is never shown as an exact figure.
 */
function formatMonthlySold(product) {
  const display = product?.monthly_sold_display;
  if (typeof display === "string" && display.trim()) return display.trim();
  const bound = product?.monthly_sold_lower_bound;
  if (typeof bound === "number" && Number.isFinite(bound)) {
    return product?.monthly_sold_is_lower_bound === false
      ? bound.toLocaleString()
      : `${bound.toLocaleString()}+`;
  }
  if (product?.monthly_sold_lower_bound === null && product?.monthly_sold_is_lower_bound === false) {
    return "—";
  }
  return formatSold(product?.monthly_sold);
}

function monthlySoldCaption(product) {
  if (product?.monthly_sold_source === "walmart_bought_past_month") {
    return product?.monthly_sold_is_lower_bound === false ? "Bought last month" : "Bought last month (min)";
  }
  if (product?.enrichment?.demand === "unsupported") return "Unavailable";
  return "Demand signal";
}

function BuyBoxMetric({ product, check, onCheck, disabled }) {
  const enrich = product?.enrichment?.buy_box;
  const status = check?.status || "idle";
  const knownPrice =
    status === "success"
      ? check.price
      : product.buy_box_price != null
        ? product.buy_box_price
        : null;
  const knownSeller =
    status === "success"
      ? check.seller
      : product.buy_box_owner != null
        ? product.buy_box_owner
        : null;

  let body;
  if (status === "loading" || enrich === "pending" || enrich === "loading" || enrich === "refreshing") {
    // A refresh with a known value must keep showing it (never "Loading…").
    if (knownPrice != null || knownSeller) {
      const soldBy = formatBuyBoxSoldBy(knownSeller);
      body = (
        <>
          <strong>{knownPrice != null ? money(Number(knownPrice)) : "—"}</strong>
          {soldBy ? (
            <span className="sfh-buybox-seller-badge" title={soldBy}>
              <span className="sfh-buybox-seller-badge__text">{soldBy}</span>
            </span>
          ) : null}
        </>
      );
    } else {
      body = (
        <>
          <span className="sfh-metric-skeleton" aria-hidden="true" />
          <span className="sfh-metric-skeleton sfh-metric-skeleton--short" aria-hidden="true" />
        </>
      );
    }
  } else if (status === "error" || enrich === "error") {
    body = (
      <>
        <strong>—</strong>
        <button type="button" className="sfh-metric-check-btn" onClick={onCheck} disabled={disabled}>
          Retry
        </button>
      </>
    );
  } else if (status === "empty") {
    body = (
      <>
        <strong>—</strong>
        <small>No current Buy Box</small>
      </>
    );
  } else if (knownPrice != null || knownSeller) {
    // Owner-only (price unknown) is still a successful Buy Box signal — do not
    // collapse to a bare "—". Never invent a price from storefront_price.
    const soldBy = formatBuyBoxSoldBy(knownSeller);
    const walmartOwned = isWalmartBuyBoxSeller(knownSeller);
    body = (
      <>
        <strong>{knownPrice != null ? money(Number(knownPrice)) : "—"}</strong>
        {soldBy ? (
          <span
            className={
              walmartOwned
                ? "sfh-buybox-seller-badge sfh-buybox-seller-badge--walmart"
                : "sfh-buybox-seller-badge sfh-buybox-seller-badge--third-party"
            }
            data-testid="sfh-buybox-seller-badge"
            title={soldBy}
          >
            <Store className="sfh-buybox-seller-badge__icon" aria-hidden />
            <span className="sfh-buybox-seller-badge__text">{soldBy}</span>
          </span>
        ) : null}
      </>
    );
  } else if (enrich === "unavailable" || enrich === "complete") {
    body = (
      <>
        <strong>—</strong>
      </>
    );
  } else {
    // Automatic enrichment scheduled — never prompt manual Buy Box check.
    body = (
      <>
        <strong className="sfh-metric-checking">Loading…</strong>
      </>
    );
  }

  return (
    <article className="sfh-metric" data-column="buybox" data-testid="sfh-metric-buybox">
      <span className="sfh-metric-label">Buy Box</span>
      {body}
    </article>
  );
}

function SellersMetric({ product, check, onCheck, disabled }) {
  const enrich = product?.enrichment?.seller_count;
  const status = check?.status || "idle";
  const enrichTerminal = enrich === "complete" || enrich === "unavailable";
  const productCount =
    typeof product?.seller_count === "number" && Number.isFinite(product.seller_count)
      ? product.seller_count
      : null;
  const checkCount =
    (status === "success" || status === "empty") &&
    typeof check?.count === "number" &&
    Number.isFinite(check.count)
      ? check.count
      : null;

  // Prefer a confident multi-offer count from either enrich or market-check.
  // Never let an unknown/unavailable enrich fabricate display as 1 when check has better data.
  let knownCount = null;
  if (productCount != null && enrichTerminal) {
    knownCount = productCount;
  }
  if (checkCount != null) {
    if (knownCount == null || (knownCount <= 1 && checkCount > knownCount)) {
      knownCount = checkCount;
    }
  }
  if (knownCount == null && productCount != null) {
    knownCount = productCount;
  }

  let body;
  if (status === "loading" || enrich === "pending" || enrich === "loading" || enrich === "refreshing") {
    if (knownCount != null) {
      body = (
        <>
          <strong>{Number(knownCount).toLocaleString()}</strong>
          <small>Walmart offers</small>
        </>
      );
    } else {
      body = (
        <>
          <span className="sfh-metric-skeleton" aria-hidden="true" />
          <span className="sfh-metric-skeleton sfh-metric-skeleton--short" aria-hidden="true" />
        </>
      );
    }
  } else if (status === "error" || enrich === "error") {
    body = (
      <>
        <strong>—</strong>
        <button type="button" className="sfh-metric-check-btn" onClick={onCheck} disabled={disabled}>
          Retry
        </button>
      </>
    );
  } else if ((status === "empty" || knownCount === 0) && knownCount != null) {
    body = (
      <>
        <strong>0</strong>
        <small>No Walmart offers</small>
      </>
    );
  } else if (knownCount != null) {
    body = (
      <>
        <strong>{Number(knownCount).toLocaleString()}</strong>
        <small>Walmart offers</small>
      </>
    );
  } else if (enrich === "unavailable" || enrich === "complete") {
    body = <strong>—</strong>;
  } else {
    body = <strong className="sfh-metric-checking">Loading…</strong>;
  }

  return (
    <article
      className="sfh-metric"
      data-column="sellers"
      data-testid="sfh-metric-sellers"
      data-seller-count-source={product?.seller_count_source || "unknown"}
      data-seller-count={knownCount == null ? "" : String(knownCount)}
    >
      <span className="sfh-metric-label">Sellers</span>
      {body}
    </article>
  );
}

/**
 * Buy Sources count from the page-owned cached search entry — never fetches.
 * Distinct from Walmart Marketplace seller_count on the Sellers metric.
 */
function BuySourcesMetric({ buySourcesEntry }) {
  const status = buySourcesEntry?.status || BUY_SOURCES_STATUS.IDLE;
  const searching =
    status === BUY_SOURCES_STATUS.QUEUED || status === BUY_SOURCES_STATUS.SEARCHING;

  let body;
  if (searching || status === BUY_SOURCES_STATUS.IDLE) {
    body = <strong className="sfh-metric-checking">Loading…</strong>;
  } else if (status === BUY_SOURCES_STATUS.ERROR) {
    body = <strong>—</strong>;
  } else {
    const count = countBuySourcesFromEntry(buySourcesEntry) ?? 0;
    body = (
      <>
        <strong data-testid="sfh-metric-buy-sources-count">
          {Number(count).toLocaleString()}
        </strong>
        <small>External sources</small>
      </>
    );
  }

  return (
    <article
      className="sfh-metric"
      data-column="buy-sources-count"
      data-testid="sfh-metric-buy-sources"
    >
      <span className="sfh-metric-label">Buy Sources</span>
      {body}
    </article>
  );
}

/**
 * Buy Sources loading = skeleton cards in the real rail geometry.
 * No textual "Loading…/Searching…" state; shapes only.
 */
function SourceSkeletonRail({ count = 5 }) {
  return (
    <div
      className="sfh-source-slider sfh-source-skeleton-rail"
      data-testid="sfh-buy-sources-loading"
      aria-busy="true"
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4].slice(0, count).map((i) => (
        <div
          key={i}
          className="sfh-source-skeleton-card"
          data-testid="sfh-buy-sources-skeleton-card"
        >
          <span className="sfh-skeleton-block sfh-skeleton-favicon" />
          <span className="sfh-skeleton-copy">
            <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-line-title" />
            <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-line-price" />
          </span>
          <span className="sfh-skeleton-block sfh-skeleton-external" />
        </div>
      ))}
    </div>
  );
}

/**
 * Compact product card — five KPIs + Quick ROI + automatic Buy Sources.
 * Buy Sources state is owned by the page and keyed by walmart_item_id.
 */
export function StorefrontProductCard({
  product,
  columns = {},
  onAnalyze,
  marketCheck,
  onCheckBuyBox,
  onCheckSellers,
  buySourcesState = null,
  onRetryBuySources,
  onRefreshBuySources,
  saved = false,
  saveBusy = false,
  onToggleSave,
}) {
  const show = (key) => columns[key] !== false;
  const itemId = product.walmart_item_id || product.id;

  const buySources = buySourcesState || emptyBuySourcesEntry(BUY_SOURCES_STATUS.IDLE);
  const buySourcesStatus = buySources.status || BUY_SOURCES_STATUS.IDLE;

  const ratingLabel = formatRating(product.rating);

  const metaBits = [];
  if (product.brand) metaBits.push({ key: "brand", node: product.brand });
  if (show("rating") && ratingLabel != null) {
    const reviews =
      show("reviews") && product.review_count != null
        ? ` (${Number(product.review_count).toLocaleString()})`
        : "";
    metaBits.push({
      key: "rating",
      node: (
        <>
          <RatingStar className="mr-0.5" />
          {ratingLabel}
          {reviews}
        </>
      ),
    });
  }

  const effectiveSalePrice = useMemo(() => {
    if (marketCheck?.buyBox?.status === "success" && marketCheck.buyBox.price != null) {
      return marketCheck.buyBox.price;
    }
    if (product.buy_box_price != null) return product.buy_box_price;
    return product.storefront_price ?? null;
  }, [marketCheck, product.buy_box_price, product.storefront_price]);

  const displaySources = useMemo(() => {
    if (
      buySourcesStatus !== BUY_SOURCES_STATUS.COMPLETE &&
      buySourcesStatus !== BUY_SOURCES_STATUS.UNRESOLVED
    ) {
      return [];
    }
    return visibleBuySourcesFromEntry(buySources);
  }, [buySources, buySourcesStatus]);

  const searching =
    buySourcesStatus === BUY_SOURCES_STATUS.QUEUED ||
    buySourcesStatus === BUY_SOURCES_STATUS.SEARCHING;

  const catalogSizes = useMemo(
    () => collectCatalogSizes({
      variations: product?.variations || product?.bundle?.variations || [],
      productDetails: product?.product_details || product?.bundle?.product_details || [],
      currentItemId: product?.walmart_item_id || product?.id,
    }),
    [product?.variations, product?.bundle?.variations, product?.product_details, product?.bundle?.product_details, product?.walmart_item_id, product?.id],
  );
  const keyFeatures = useMemo(
    () => (Array.isArray(product?.key_features) ? product.key_features.filter(Boolean) : []),
    [product?.key_features],
  );
  const { description: normalizedDescription, features: normalizedFeatures } = useMemo(
    () => normalizeProductDescription(product?.description || "", keyFeatures),
    [product?.description, keyFeatures],
  );
  const hasDescription = normalizedDescription.length > 0 || normalizedFeatures.length > 0;
  const hasCatalogSizes = catalogSizes.length > 0;

  const [descOpen, setDescOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const detailsBtnRef = useRef(null);
  const catalogBtnRef = useRef(null);

  useEffect(() => {
    setDescOpen(false);
    setCatalogOpen(false);
  }, [product?.id, product?.walmart_item_id, product?.title]);

  const closeDescription = useCallback(({ returnFocus = false } = {}) => {
    setDescOpen(false);
    if (returnFocus) queueMicrotask(() => detailsBtnRef.current?.focus?.());
  }, []);
  const closeCatalog = useCallback(({ returnFocus = false } = {}) => {
    setCatalogOpen(false);
    if (returnFocus) queueMicrotask(() => catalogBtnRef.current?.focus?.());
  }, []);
  const toggleDescription = useCallback(() => {
    setCatalogOpen(false);
    setDescOpen((o) => !o);
  }, []);
  const toggleCatalog = useCallback(() => {
    setDescOpen(false);
    setCatalogOpen((o) => !o);
  }, []);

  const sourceActions = useMemo(
    () => buildStorefrontHunterSourceActions({
      title: product.title,
      walmartItemId: product.walmart_item_id || product.id,
      buySourcesEntry: buySources,
      catalogAction: hasCatalogSizes
        ? {
            enabled: true,
            onClick: toggleCatalog,
            ariaExpanded: catalogOpen,
            ariaControls: CATALOG_POPOVER_ID,
            testId: "catalog-sizes-trigger",
          }
        : { enabled: false },
      detailsAction: hasDescription
        ? {
            enabled: true,
            onClick: toggleDescription,
            ariaExpanded: descOpen,
            ariaControls: DESCRIPTION_POPOVER_ID,
            testId: "product-description-trigger",
          }
        : { enabled: false },
    }),
    [
      product.title,
      product.walmart_item_id,
      product.id,
      buySources,
      hasCatalogSizes,
      hasDescription,
      toggleCatalog,
      toggleDescription,
      catalogOpen,
      descOpen,
    ],
  );

  const productImages = useMemo(() => {
    const gallery = product.image_urls || product.images;
    if (Array.isArray(gallery) && gallery.length) {
      return gallery.filter(Boolean);
    }
    return product.image_url ? [product.image_url] : [];
  }, [product.image_url, product.image_urls, product.images]);

  return (
    <article className="sfh-product-card" data-testid={`sfh-product-${product.id}`} data-sfh-item-id={itemId ? String(itemId).trim() || undefined : undefined}>
      <div className="sfh-product-head">
        <div className="sfh-product-image" data-testid="sfh-product-image-loupe">
          <ProductImageLoupe
            images={productImages}
            title={product.title}
            size={72}
            zoomPanelSize={140}
            lensSize={36}
            zoomBackgroundSize={280}
            showThumbnails={false}
            showNav={productImages.length > 1}
            placeholder={<span className="text-[9px] text-ink-muted">No image</span>}
          />
        </div>

        <div className="sfh-product-info min-w-0">
          <div className="sfh-title-row">
            <div className="sfh-title-copy">
              <h3 className="sfh-product-title">{product.title}</h3>
              {(metaBits.length || product.title) ? (
                <div className="sfh-product-meta">
                  {metaBits.length ? (
                    <span className="sfh-product-meta-text">
                      {metaBits.map((bit, i) => (
                        <React.Fragment key={bit.key}>
                          {i > 0 ? " · " : null}
                          {bit.node}
                        </React.Fragment>
                      ))}
                    </span>
                  ) : null}
                  <ProductSourceIconBar
                    active={catalogOpen ? "catalog" : descOpen ? "details" : null}
                    actions={sourceActions}
                    detailsRef={detailsBtnRef}
                    catalogRef={catalogBtnRef}
                  />
                  <ProductDescriptionPopover
                    open={descOpen}
                    onClose={closeDescription}
                    anchorRef={detailsBtnRef}
                    description={normalizedDescription}
                    features={normalizedFeatures}
                  />
                  <CatalogSizesPopover
                    open={catalogOpen}
                    onClose={closeCatalog}
                    anchorRef={catalogBtnRef}
                    sizes={catalogSizes}
                  />
                </div>
              ) : null}
              {show("walmart_item_id") && product.walmart_item_id ? (
                <div className="sfh-identifiers">
                  Walmart Item ID <strong>{product.walmart_item_id}</strong>
                </div>
              ) : null}
              {show("upc") && product.upc ? (
                <div className="sfh-identifiers">
                  UPC <strong>{product.upc}</strong>
                </div>
              ) : null}
            </div>

            <div className="sfh-head-actions">
              {product.added_days_ago != null && product.added_days_ago <= 7 ? (
                <span className="sfh-new-pill">New · {product.added_days_ago}d</span>
              ) : null}
              {onToggleSave ? (
                <button
                  type="button"
                  className={`sfh-save-product${saved ? " saved" : ""}`}
                  aria-pressed={saved}
                  aria-label={saved ? "Unsave product" : "Save product"}
                  title={saved ? "Unsave product" : "Save product"}
                  disabled={saveBusy}
                  data-testid={`sfh-save-product-${itemId}`}
                  onClick={() => onToggleSave(product, !saved)}
                >
                  {saved ? (
                    <svg className="sfh-bookmark-icon" viewBox="0 0 24 24" aria-hidden="true" data-testid="sfh-bookmark-filled">
                      <path d="M6 3.75A1.75 1.75 0 0 1 7.75 2h8.5A1.75 1.75 0 0 1 18 3.75V22l-6-3.9L6 22V3.75Z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg className="sfh-bookmark-icon" viewBox="0 0 24 24" aria-hidden="true" data-testid="sfh-bookmark-outline">
                      <path
                        d="M6.75 2.75h10.5v16.87L12 16.2l-5.25 3.42V2.75Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              ) : null}
              <button type="button" className="sfh-analyze" onClick={() => onAnalyze?.(product)}>
                Analyze
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sfh-metrics" data-testid="sfh-primary-metrics">
        {show("storefront_price") ? (
          <article className="sfh-metric" data-column="store-price">
            <span className="sfh-metric-label">Storefront Price</span>
            <strong>{money(product.storefront_price)}</strong>
            <small>This seller</small>
          </article>
        ) : null}
        {show("buy_box_price") ? (
          <BuyBoxMetric
            product={product}
            check={marketCheck?.buyBox}
            onCheck={() => onCheckBuyBox?.(itemId)}
            disabled={!itemId || marketCheck?.buyBox?.status === "loading"}
          />
        ) : null}
        {show("max_cost") ? (
          <article className="sfh-metric" data-column="max-cost">
            <span className="sfh-metric-label">Max Cost</span>
            <strong>{money(product.max_cost)}</strong>
            <small>30% target ROI</small>
          </article>
        ) : null}
        {show("seller_count") ? (
          <SellersMetric
            product={product}
            check={marketCheck?.sellers}
            onCheck={() => onCheckSellers?.(itemId)}
            disabled={!itemId || marketCheck?.sellers?.status === "loading"}
          />
        ) : null}
        {show("buy_sources") ? <BuySourcesMetric buySourcesEntry={buySources} /> : null}
        {show("monthly_sold") ? (
          <article className="sfh-metric" data-column="monthly">
            <span className="sfh-metric-label">Monthly Sold</span>
            <strong data-testid="sfh-monthly-sold">
              {formatMonthlySold(product)}
            </strong>
            <small data-testid="sfh-monthly-sold-source">
              {monthlySoldCaption(product)}
            </small>
          </article>
        ) : null}
      </div>

      {show("quick_roi") ? (
        <QuickROI product={product} salePrice={effectiveSalePrice} />
      ) : null}

      {show("buy_sources") ? (
        <section className="sfh-sources" data-testid="sfh-buy-sources">
          <header className="sfh-source-head">
            <strong>Buy Sources</strong>
            {buySourcesStatus === BUY_SOURCES_STATUS.ERROR ? (
              <button
                type="button"
                className="sfh-find-sources-btn"
                onClick={() => onRetryBuySources?.(product)}
                data-testid="sfh-buy-sources-retry"
              >
                Retry
              </button>
            ) : buySourcesStatus === BUY_SOURCES_STATUS.COMPLETE ||
              buySourcesStatus === BUY_SOURCES_STATUS.NO_MATCH ||
              buySourcesStatus === BUY_SOURCES_STATUS.UNRESOLVED ? (
              <button
                type="button"
                className="sfh-find-sources-btn sfh-find-sources-btn--secondary"
                onClick={() => onRefreshBuySources?.(product)}
                data-testid="sfh-buy-sources-refresh"
              >
                Refresh
              </button>
            ) : null}
          </header>

          {searching ? (
            <SourceSkeletonRail />
          ) : displaySources.length > 0 ? (
            <BuySourcesRail
              sources={displaySources}
              testId="sfh-buy-sources-rail"
              walmartTargetPrice={
                buySources.walmart_target_price ?? effectiveSalePrice
              }
              hideVerificationBadge
            />
          ) : buySourcesStatus === BUY_SOURCES_STATUS.NO_MATCH ? (
            <p className="sfh-sources-msg" data-testid="sfh-buy-sources-empty">
              No sourcing match found
            </p>
          ) : buySourcesStatus === BUY_SOURCES_STATUS.ERROR ? (
            <p
              className="sfh-sources-msg"
              data-testid="sfh-buy-sources-error"
              data-failure-reason={
                buySources.buy_sources_failure_reason ||
                buySources.failure_reason ||
                buySources.error ||
                "unknown"
              }
              data-buy-sources-failure-reason={
                buySources.buy_sources_failure_reason ||
                buySources.failure_reason ||
                buySources.error ||
                "unknown"
              }
              data-buy-sources-request-id={
                buySources.request_id ||
                buySources.diagnostics?.request_id ||
                undefined
              }
              data-buy-sources-item-id={
                buySources.diagnostics?.walmart_item_id ||
                product?.walmart_item_id ||
                undefined
              }
            >
              Source search unavailable
            </p>
          ) : buySourcesStatus === BUY_SOURCES_STATUS.UNRESOLVED ? (
            <p className="sfh-sources-msg" data-testid="sfh-buy-sources-empty">
              No sourcing match found
            </p>
          ) : null}
        </section>
      ) : null}
    </article>
  );
}
